"use client";

import { useState } from "react";
import Swal from "sweetalert2";

import {
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Clock3,
  FileCheck2,
  FileText,
  GraduationCap,
  Globe2,
  HeartHandshake,
  LockKeyhole,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  Truck,
  Users,
  X,
} from "lucide-react";

import "./UaeLp.css";
import logo from "./../../../vid_img/logo.png";

/* =========================================================
   COUNTRIES
========================================================= */

const countries = [
  ["🇦🇪", "UAE"],
  ["🇸🇦", "Saudi Arabia"],
  ["🇶🇦", "Qatar"],
  ["🇴🇲", "Oman"],
  ["🇰🇼", "Kuwait"],
  ["🇧🇭", "Bahrain"],
  ["🇬🇧", "UK"],
  ["🇺🇸", "USA"],
  ["🇨🇦", "Canada"],
  ["🇦🇺", "Australia"],
];

/* =========================================================
   DOCUMENTS
========================================================= */

const docs = [
  {
    icon: GraduationCap,
    title: "Degree Certificate",
  },
  {
    icon: FileText,
    title: "Diploma Certificate",
  },
  {
    icon: "💍",
    title: "Marriage Certificate",
  },
  {
    icon: "👣",
    title: "Birth Certificate",
  },
  {
    icon: ShieldCheck,
    title: "PCC (Police Clearance)",
  },
  {
    icon: Building2,
    title: "Commercial Documents",
  },
];

/* =========================================================
   FAQS
========================================================= */

const faqs = [
  "What is UAE Embassy Attestation?",
  "Which documents require UAE attestation?",
  "How does the UAE attestation process work from Bangalore?",
  "How long does UAE Embassy Attestation take?",
  "Can you arrange document pickup in Bangalore?",
  "What documents are needed for UAE attestation?",
  "Do I need to visit your office?",
  "Do you also provide UAE MoFA attestation support?",
];

/* =========================================================
   REVIEWS
========================================================= */

const reviews = [
  {
    name: "Rahul S.",
    city: "Bangalore",
    type: "UAE Attestation",
    text: "Very professional service. They guided me through the document process and kept me informed throughout.",
  },
  {
    name: "Priya M.",
    city: "Bangalore",
    type: "Marriage Certificate",
    text: "Smooth and hassle-free experience. Clear guidance and quick communication from the support team.",
  },
  {
    name: "Arun K.",
    city: "Bangalore",
    type: "PCC Attestation",
    text: "Excellent service and customer support. Helped me understand the document requirements for the UAE.",
  },
];

/* =========================================================
   BRIEFCASE ICON
========================================================= */

function BriefcaseIcon() {
  return (
    <span className="uaeLp-briefcase-emoji">
      💼
    </span>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

const UaeLp = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <main id="top">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="uaeLp-site-header">
        <div className="uaeLp-container uaeLp-header-inner">

          <a href="#top" onClick={closeMenu}>
            <img
              src={logo}
              alt="Goodway Attestation"
              className="uaeLp-logo"
            />
          </a>

          <nav
            className={`uaeLp-nav ${
              menuOpen ? "uaeLp-nav-open" : ""
            }`}
          >
            <a
              href="#top"
              className="uaeLp-active"
              onClick={closeMenu}
            >
              Home
            </a>

            <a href="#process" onClick={closeMenu}>
              Process
            </a>

            <a href="#documents" onClick={closeMenu}>
              Document Types
            </a>

            <a href="#services" onClick={closeMenu}>
              UAE Attestation
            </a>

            <a href="#faq" onClick={closeMenu}>
              FAQs
            </a>

            <a href="#location" onClick={closeMenu}>
              Contact
            </a>
          </nav>

          <div className="uaeLp-header-actions">

            <a
              href="tel:+919148889444"
              className="uaeLp-call-pill"
            >
              <Phone size={17} />
              Call Us
            </a>

            <a
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
              className="uaeLp-wa-pill"
            >
              <MessageCircle size={17} />
              WhatsApp
            </a>

            <button
              type="button"
              className="uaeLp-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </button>

          </div>
        </div>
      </header>

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="uaeLp-hero">

        <div className="uaeLp-hero-bg-glow"></div>

        <div className="uaeLp-container uaeLp-hero-grid">

          {/* HERO CONTENT */}

          <div className="uaeLp-hero-copy">

            <div className="uaeLp-eyebrow">
              <span className="uaeLp-dot"></span>
              UAE EMBASSY ATTESTATION IN BANGALORE
            </div>

            <h1>
              Get Your Documents Attested
              <br />
              for the UAE in <span>Bangalore</span>
            </h1>

            <p>
              Professional UAE Embassy Attestation assistance for
              educational, personal and commercial documents, with
              document checking, process guidance and doorstep pickup
              across Bangalore.
            </p>

            {/* FEATURES */}

            <div className="uaeLp-feature-row">

              <div>
                <span className="uaeLp-feature-icon">
                  <Check size={15} />
                </span>
                Doorstep Pickup
              </div>

              <div>
                <span className="uaeLp-feature-icon">
                  <Check size={15} />
                </span>
                Document Guidance
              </div>

              <div>
                <span className="uaeLp-feature-icon">
                  <Check size={15} />
                </span>
                Process Updates
              </div>

            </div>

            {/* TRUST */}

            <div className="uaeLp-trust-row">

              <div className="uaeLp-google-rating">

                <div className="uaeLp-g">
                  G
                </div>

                <div>
                  <div className="uaeLp-stars">
                    ★★★★★
                  </div>

                  <small>
                    4.8/5 Google Rating
                  </small>
                </div>

              </div>

              <div className="uaeLp-avatars">
                <span>R</span>
                <span>P</span>
                <span>A</span>
                <span>+</span>
              </div>

              <p>
                Trusted by <strong>2,400+</strong>
                <br />
                happy customers
              </p>

            </div>

            {/* HERO BUTTONS */}

            <div className="uaeLp-hero-buttons">

              <a
                href="#quote"
                className="uaeLp-btn uaeLp-btn-orange"
              >
                Get Free UAE Attestation Quote
                <ArrowRight size={17} />
              </a>

              <a
                href="https://wa.me/919148889444"
                target="_blank"
                rel="noreferrer"
                className="uaeLp-hero-wa"
              >
                <MessageCircle size={19} />
                WhatsApp an Expert
              </a>

            </div>
          </div>

          {/* =================================================
              HERO VISUAL
          ================================================= */}

          <div className="uaeLp-hero-visual">

            <div className="uaeLp-hero-art"></div>

            {/* QUOTE FORM */}

            <QuoteForm />

          </div>
        </div>
      </section>

      {/* =====================================================
          PROCESS
      ===================================================== */}

      <section
        className="uaeLp-section uaeLp-process"
        id="process"
      >
        <div className="uaeLp-container">

          <div className="uaeLp-section-kicker">
            SIMPLE. TRANSPARENT. GUIDED.
          </div>

          <h2>
            How UAE Attestation Works
          </h2>

          <p className="uaeLp-section-lead">
            Get your documents prepared for UAE use through a clear
            step-by-step process.
          </p>

          <div className="uaeLp-steps">

            {/* STEP 1 */}

            <div className="uaeLp-step-wrap">

              <div className="uaeLp-step-card">

                <div className="uaeLp-step-no">
                  01
                </div>

                <FileCheck2 size={40} />

                <h3>
                  Share Your Details
                </h3>

                <p>
                  Tell us your document type, purpose and UAE
                  requirement.
                </p>

              </div>

              <ArrowRight className="uaeLp-step-arrow" />

            </div>

            {/* STEP 2 */}

            <div className="uaeLp-step-wrap">

              <div className="uaeLp-step-card">

                <div className="uaeLp-step-no">
                  02
                </div>

                <Building2 size={40} />

                <h3>
                  Document Check
                </h3>

                <p>
                  We review your document and explain the applicable
                  UAE attestation route.
                </p>

              </div>

              <ArrowRight className="uaeLp-step-arrow" />

            </div>

            {/* STEP 3 */}

            <div className="uaeLp-step-wrap">

              <div className="uaeLp-step-card">

                <div className="uaeLp-step-no">
                  03
                </div>

                <Truck size={40} />

                <h3>
                  Attestation Process
                </h3>

                <p>
                  Your document is processed through the relevant
                  authorities for UAE use.
                </p>

              </div>

            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          DOCUMENTS
      ===================================================== */}

      <section
        className="uaeLp-section uaeLp-documents"
        id="documents"
      >
        <div className="uaeLp-container">

          <div className="uaeLp-section-kicker">
            POPULAR DOCUMENT TYPES
          </div>

          <h2>
            Which Document Needs UAE Attestation?
          </h2>

          <p className="uaeLp-section-lead">
            Choose your document type to understand the UAE attestation
            requirements and next steps.
          </p>

          <div className="uaeLp-doc-grid">

            {docs.map((doc) => {

              const Icon = doc.icon;

              return (
                <div
                  className="uaeLp-doc-card"
                  key={doc.title}
                >

                  <div className="uaeLp-doc-icon">

                    {typeof Icon === "string" ? (
                      <span>
                        {Icon}
                      </span>
                    ) : (
                      <Icon size={32} />
                    )}

                  </div>

                  <h3>
                    {doc.title}
                  </h3>

                  <p>
                    Get document-specific guidance for UAE Embassy
                    Attestation and authentication requirements.
                  </p>

                  <a href="#quote">
                    Get Quote
                    <ArrowRight size={15} />
                  </a>

                </div>
              );
            })}

          </div>

          {/* DOCUMENT HELP */}

          <div className="uaeLp-document-help">

            <div>
              <strong>
                Don't see your document?
              </strong>

              <p>
                Talk to our expert for document-specific UAE
                attestation guidance.
              </p>
            </div>

            <a
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
              className="uaeLp-btn uaeLp-btn-orange"
            >
              <MessageCircle size={18} />
              Talk to an Expert
            </a>

          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICES
      ===================================================== */}

      <section
        className="uaeLp-section uaeLp-services"
        id="services"
      >
        <div className="uaeLp-container">

          <div className="uaeLp-services-grid">

            {/* INTRO */}

            <div className="uaeLp-services-intro">

              <div className="uaeLp-section-kicker">
                UAE ATTESTATION SUPPORT
              </div>

              <h2>
                UAE Embassy Attestation
                <br />
                Services We Provide
              </h2>

              <p>
                Document-specific assistance with requirement checking,
                authentication coordination and UAE attestation process
                guidance.
              </p>

              <a
                href="#quote"
                className="uaeLp-btn uaeLp-btn-orange"
              >
                Get Free Quote
                <ArrowRight size={16} />
              </a>

            </div>

            {/* EDUCATIONAL */}

            <div className="uaeLp-service-card">

              <GraduationCap size={36} />

              <h3>
                Educational Documents
              </h3>

              <p>
                Degree certificates, diplomas, mark sheets and
                educational transcripts for UAE use.
              </p>

              <a href="#quote">
                Get Quote
                <ArrowRight size={15} />
              </a>

            </div>

            {/* PERSONAL */}

            <div className="uaeLp-service-card">

              <HeartHandshake size={36} />

              <h3>
                Personal Documents
              </h3>

              <p>
                Birth certificates, marriage certificates, single
                status certificates and PCC.
              </p>

              <a href="#quote">
                Get Quote
                <ArrowRight size={15} />
              </a>

            </div>

            {/* COMMERCIAL */}

            <div className="uaeLp-service-card">

              <BriefcaseIcon />

              <h3>
                Commercial Documents
              </h3>

              <p>
                Company documents, power of attorney, agreements,
                invoices, MOA and AOA.
              </p>

              <a href="#quote">
                Get Quote
                <ArrowRight size={15} />
              </a>

            </div>

            {/* EMBASSY */}

            <div className="uaeLp-service-card">

              <Globe2 size={36} />

              <h3>
                UAE Embassy Attestation
              </h3>

              <p>
                Complete guidance for documents requiring UAE Embassy
                Attestation and UAE MoFA support.
              </p>

              <a href="#quote">
                Get Quote
                <ArrowRight size={15} />
              </a>

            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          WHY GOODWAY
      ===================================================== */}

      <section className="uaeLp-section uaeLp-why">

        <div className="uaeLp-container">

          <div className="uaeLp-section-kicker">
            WHY GOODWAY
          </div>

          <h2>
            Why Choose Goodway?
          </h2>

          <p className="uaeLp-section-lead">
            Reliable. Experienced. Customer-Focused.
          </p>

          <div className="uaeLp-why-grid">

            {/* RELIABLE */}

            <div className="uaeLp-why-card">

              <div className="uaeLp-why-icon">
                <ShieldCheck />
              </div>

              <h3>
                Experienced Team
              </h3>

              <p>
                Years of experience supporting customers with
                attestation and document requirements.
              </p>

            </div>

            {/* QUICK */}

            <div className="uaeLp-why-card">

              <div className="uaeLp-why-icon">
                <Clock3 />
              </div>

              <h3>
                Transparent Process
              </h3>

              <p>
                Clear communication and regular updates throughout
                your UAE attestation process.
              </p>

            </div>

            {/* PICKUP */}

            <div className="uaeLp-why-card">

              <div className="uaeLp-why-icon">
                <Truck />
              </div>

              <h3>
                Bangalore Pickup
              </h3>

              <p>
                Convenient document pickup options available across
                Bangalore.
              </p>

            </div>

            {/* SUPPORT */}

            <div className="uaeLp-why-card">

              <div className="uaeLp-why-icon">
                <Users />
              </div>

              <h3>
                Document Assistance
              </h3>

              <p>
                Help with document requirements, checking and
                verification before processing.
              </p>

            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          COUNTRIES
      ===================================================== */}

      <section className="uaeLp-section uaeLp-countries">

        <div className="uaeLp-container">

          <div className="uaeLp-section-kicker">
            DESTINATION SUPPORT
          </div>

          <h2>
            Document Attestation for the UAE
          </h2>

          <p className="uaeLp-section-lead">
            Support for documents intended for employment, education,
            family and business purposes in the UAE.
          </p>

          <div className="uaeLp-country-grid">

            {countries.map(([flag, country]) => (
              <div
                className="uaeLp-country-card"
                key={country}
              >
                <span>
                  {flag}
                </span>

                <strong>
                  {country}
                </strong>
              </div>
            ))}

          </div>

          <a
            href="#quote"
            className="uaeLp-outline-link"
          >
            View Attestation Services
            <ArrowRight size={16} />
          </a>

        </div>
      </section>

      {/* =====================================================
          REVIEWS
      ===================================================== */}

      <section className="uaeLp-section uaeLp-reviews">

        <div className="uaeLp-container">

          <div className="uaeLp-section-kicker">
            CUSTOMER REVIEWS
          </div>

          <h2>
            What Our Customers Say
          </h2>

          <p className="uaeLp-section-lead">
            Feedback from people who trusted Goodway with their
            documents.
          </p>

          <div className="uaeLp-review-grid">

            {reviews.map((review) => (
              <article
                className="uaeLp-review-card"
                key={review.name}
              >

                <div className="uaeLp-review-stars">
                  ★★★★★
                </div>

                <p>
                  "{review.text}"
                </p>

                <div className="uaeLp-review-author">

                  <div className="uaeLp-person-avatar">
                    {review.name.charAt(0)}
                  </div>

                  <div>
                    <strong>
                      {review.name}
                    </strong>

                    <span>
                      {review.city} • {review.type}
                    </span>
                  </div>

                </div>
              </article>
            ))}

          </div>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section
        className="uaeLp-section uaeLp-faqs"
        id="faq"
      >

        <div className="uaeLp-container">

          <div className="uaeLp-section-kicker">
            FREQUENTLY ASKED QUESTIONS
          </div>

          <h2>
            Your UAE Attestation Questions, Answered
          </h2>

          <p className="uaeLp-section-lead">
            Find answers to common questions about UAE Embassy
            Attestation in Bangalore.
          </p>

          <div className="uaeLp-faq-grid">

            {faqs.map((faq, index) => (
              <div
                className={`uaeLp-faq ${
                  openFaq === index
                    ? "uaeLp-open"
                    : ""
                }`}
                key={faq}
              >

                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                >
                  <span>
                    {faq}
                  </span>

                  <ChevronDown size={20} />
                </button>

                {openFaq === index && (
                  <div className="uaeLp-faq-answer">
                    <p>
                      The exact requirements depend on the document
                      type, issuing authority and purpose. Contact
                      Goodway Attestation for a document-specific
                      assessment before processing.
                    </p>
                  </div>
                )}

              </div>
            ))}

          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section className="uaeLp-final-cta">

        <div className="uaeLp-container uaeLp-final-cta-inner">

          <div>

            <span>
              UAE EMBASSY ATTESTATION SUPPORT
            </span>

            <h2>
              Need UAE Embassy Attestation in Bangalore?
            </h2>

            <p>
              Tell us your document and UAE requirement. Our team
              will guide you through the next steps.
            </p>

          </div>

          <div className="uaeLp-final-cta-actions">

            <a
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
              className="uaeLp-final-wa"
            >
              <MessageCircle size={19} />
              WhatsApp an Expert
            </a>

            <a
              href="tel:+919148889444"
              className="uaeLp-final-phone"
            >
              <Phone size={19} />
              Call an Expert
            </a>

          </div>
        </div>
      </section>

      {/* =====================================================
          LOCATION
      ===================================================== */}

      <section
        className="uaeLp-section uaeLp-location"
        id="location"
      >

        <div className="uaeLp-container">

          <div className="uaeLp-location-grid">

            {/* MAP */}

            <div className="uaeLp-map-card">

              <iframe
                title="Goodway Attestation Bangalore Location"
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7775.058287553612!2d77.572151!3d13.00194!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17d588d9caef%3A0x5ba382acaf05e1bd!2sGoodway%20Attestation%20%26%20Apostille%20Services%20in%20Bangalore%20Pvt.%20Ltd!5e0!3m2!1sen!2sus!4v1719668500894!5m2!1sen!2sus"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="map"
              ></iframe>

            </div>

            {/* OFFICE */}

            <div className="uaeLp-office">

              <div className="uaeLp-section-kicker">
                VISIT OUR OFFICE
              </div>

              <h2>
                Our Office in Bangalore
              </h2>

              {/* LOCATION */}

              <div className="uaeLp-info-block">

                <MapPin size={21} />

                <div>

                  <strong>
                    Office Location
                  </strong>

                  <p>
                    Bangalore, Karnataka
                    <br />
                    India
                  </p>

                </div>
              </div>

              {/* PHONE */}

              <div className="uaeLp-info-block">

                <Phone size={21} />

                <div>

                  <strong>
                    Call Us
                  </strong>

                  <p>
                    +91 9148889444
                  </p>

                </div>
              </div>

              {/* HOURS */}

              <div className="uaeLp-info-block">

                <Clock3 size={21} />

                <div>

                  <strong>
                    Working Hours
                  </strong>

                  <p>
                    Mon - Sat: 9:30 AM - 6:30 PM
                    <br />
                    Sunday: Closed
                  </p>

                </div>
              </div>

              <a
                href="#top"
                className="uaeLp-directions"
              >
                <MapPin size={17} />
                Get Directions
              </a>

            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <footer className="uaeLp-footer">

        <div className="uaeLp-container">

          <div className="uaeLp-footer-grid">

            {/* BRAND */}

            <div className="uaeLp-footer-brand">

              <img
                src={logo}
                alt="Goodway Attestation"
                className="uaeLp-logo"
              />

              <p>
                Your trusted partner for UAE Embassy Attestation and
                document attestation services in Bangalore.
              </p>

              <div className="uaeLp-socials">
                <a href="#top">f</a>
                <a href="#top">in</a>
                <a href="#top">ig</a>
              </div>

            </div>

            {/* LINKS */}

            <div>

              <h4>
                Quick Links
              </h4>

              <a href="#top">
                UAE Attestation
              </a>

              <a href="#documents">
                Document Types
              </a>

              <a href="#process">
                Process
              </a>

              <a href="#faq">
                FAQs
              </a>

              <a href="#location">
                Contact
              </a>

            </div>

            {/* SERVICES */}

            <div>

              <h4>
                Our Services
              </h4>

              <a href="#services">
                Educational Documents
              </a>

              <a href="#services">
                Personal Documents
              </a>

              <a href="#services">
                Commercial Documents
              </a>

              <a href="#services">
                UAE Embassy Attestation
              </a>

              <a href="#services">
                UAE MoFA Support
              </a>

            </div>

            {/* CONTACT */}

            <div>

              <h4>
                Contact Us
              </h4>

              <p>
                <MapPin size={16} />
                Bangalore, Karnataka
              </p>

              <p>
                <Phone size={16} />
                +91 9148889444
              </p>

              <p>
                <MessageCircle size={16} />
                WhatsApp Support
              </p>

            </div>

          </div>

          {/* FOOTER BOTTOM */}

          <div className="uaeLp-footer-bottom">

            <p>
              © {new Date().getFullYear()} Goodway
              Attestation. All Rights Reserved.
            </p>

            <div>
              <a href="#top">
                Privacy Policy
              </a>

              <a href="#top">
                Terms & Conditions
              </a>
            </div>

          </div>
        </div>
      </footer>

    </main>
  );
};

/* =========================================================
   QUOTE FORM
========================================================= */

function QuoteForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) return;

    const form = event.currentTarget;

    const formData = new FormData(form);

    /* =====================================================
       WEB3FORMS SETTINGS
    ===================================================== */

    formData.append(
      "access_key",
      "f333e4b3-a1ed-4165-a06a-0db699456e2d"
    );

    formData.append(
      "subject",
      "New UAE Attestation Quote Request"
    );

    formData.append(
      "service",
      "UAE Embassy Attestation"
    );

    const object = Object.fromEntries(
      formData.entries()
    );

    try {
      setIsSubmitting(true);

      const response = await fetch(
        "https://api.web3forms.com/submit",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          body: JSON.stringify(object),
        }
      );

      const result = await response.json();

      /* ===================================================
         SUCCESS
      =================================================== */

      if (result.success) {

        await Swal.fire({
          title: "Request Sent!",
          text: "Your UAE attestation quote request has been submitted successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });

        form.reset();

      } else {

        /* =================================================
           WEB3FORMS ERROR
        ================================================= */

        await Swal.fire({
          title: "Submission Failed",
          text:
            result.message ||
            "Failed to send your request. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });

      }

    } catch (error) {

      /* ===================================================
         NETWORK ERROR
      =================================================== */

      console.error(
        "Form submission error:",
        error
      );

      await Swal.fire({
        title: "Something Went Wrong",
        text: "Unable to submit your request. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });

    } finally {

      setIsSubmitting(false);

    }
  };

  return (
    <div
      className="uaeLp-quote-form"
      id="quote"
    >

      <h3>
        Get a Free UAE Attestation Quote
      </h3>

      <p>
        Tell us about your document and UAE requirement.
        Our team will guide you through the process,
        timeline and next steps.
      </p>

      <form onSubmit={handleSubmit}>

        {/* =================================================
            NAME + PHONE
        ================================================= */}

        <div className="uaeLp-form-row">

          <input
            type="text"
            name="name"
            placeholder="Your Name"
            autoComplete="name"
            required
          />

          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            autoComplete="tel"
            inputMode="tel"
            required
          />

        </div>

        {/* =================================================
            DOCUMENT TYPE
        ================================================= */}

        <select
          name="document_type"
          defaultValue=""
          required
        >

          <option
            value=""
            disabled
          >
            Select Document Type
          </option>

          <option value="Degree Certificate">
            Degree Certificate
          </option>

          <option value="Diploma Certificate">
            Diploma Certificate
          </option>

          <option value="Marriage Certificate">
            Marriage Certificate
          </option>

          <option value="Birth Certificate">
            Birth Certificate
          </option>

          <option value="Police Clearance Certificate">
            Police Clearance Certificate
          </option>

          <option value="Commercial Document">
            Commercial Document
          </option>

        </select>

        {/* =================================================
            DESTINATION COUNTRY
        ================================================= */}

        <select
          name="destination_country"
          defaultValue=""
          required
        >

          <option
            value=""
            disabled
          >
            Select Destination Country
          </option>

          {countries.map(([flag, country]) => (
            <option
              key={country}
              value={country}
            >
              {flag} {country}
            </option>
          ))}

        </select>

        {/* =================================================
            SUBMIT
        ================================================= */}

        <button
          type="submit"
          className="uaeLp-form-submit"
          disabled={isSubmitting}
        >

          {isSubmitting ? (
            "Sending..."
          ) : (
            <>
              Get Free UAE Attestation Quote
              <ArrowRight size={17} />
            </>
          )}

        </button>

        {/* =================================================
            SECURITY
        ================================================= */}

        <div className="uaeLp-secure">

          <LockKeyhole size={14} />

          Your information is secure. We never share your details.

        </div>

      </form>
    </div>
  );
}

export default UaeLp;
